"use client";

import * as React from "react";
import * as SwitchPrimitive from "@radix-ui/react-switch";

import { cn } from "./utils";

const switchModule = SwitchPrimitive as any;
const resolvedSwitchModule =
  typeof switchModule?.Root !== "undefined" ||
  typeof switchModule?.Thumb !== "undefined"
    ? switchModule
    : switchModule?.default ?? switchModule;
const RadixSwitchRoot: any =
  resolvedSwitchModule?.Root ?? resolvedSwitchModule?.Switch;
const RadixSwitchThumb: any =
  resolvedSwitchModule?.Thumb ?? resolvedSwitchModule?.SwitchThumb;
const hasRadixSwitch =
  typeof RadixSwitchRoot !== "undefined" &&
  typeof RadixSwitchThumb !== "undefined";

let hasWarnedMissingRadixSwitch = false;
const warnMissingRadixSwitch = () => {
  if (hasWarnedMissingRadixSwitch) return;
  hasWarnedMissingRadixSwitch = true;
  console.warn(
    "[ui/switch] Radix Switch primitives unavailable. Falling back to basic markup.",
  );
};

function Switch({
  className,
  ...props
}: React.ComponentProps<typeof SwitchPrimitive.Root>) {
  const generatedId = React.useId();
  if (!hasRadixSwitch) {
    warnMissingRadixSwitch();
    const {
      checked,
      defaultChecked,
      onCheckedChange,
      onChange,
      disabled,
      ...rest
    } = props as any;
    return (
      <input
        id={(rest as any).id ?? (rest as any).name ?? generatedId}
        data-slot="switch-fallback"
        type="checkbox"
        role="switch"
        className={cn(
          "peer data-[state=checked]:bg-primary data-[state=unchecked]:bg-switch-background focus-visible:border-ring focus-visible:ring-ring/50 dark:data-[state=unchecked]:bg-input/80 inline-flex h-[1.15rem] w-8 shrink-0 items-center rounded-full border border-transparent transition-all outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
        checked={checked}
        defaultChecked={defaultChecked}
        disabled={disabled}
        onChange={(event) => {
          onChange?.(event);
          onCheckedChange?.(event.currentTarget.checked);
        }}
        {...rest}
      />
    );
  }
  return (
    <RadixSwitchRoot
      id={(props as any).id ?? (props as any).name ?? generatedId}
      data-slot="switch"
      className={cn(
        "peer data-[state=checked]:bg-primary data-[state=unchecked]:bg-switch-background focus-visible:border-ring focus-visible:ring-ring/50 dark:data-[state=unchecked]:bg-input/80 inline-flex h-[1.15rem] w-8 shrink-0 items-center rounded-full border border-transparent transition-all outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    >
      <RadixSwitchThumb
        data-slot="switch-thumb"
        className={cn(
          // Literal colours, not bg-card. The knob is the whole affordance: if
          // its fill fails to resolve, the switch renders as a solid lozenge
          // with no visible state, which is exactly what the macOS shell was
          // showing. --card is remapped there to var(--mac-control-bg, ...),
          // so it depends on the Swift side injecting that value. A control
          // this important should not.
          "pointer-events-none block size-4 rounded-full bg-white shadow-sm ring-0 transition-transform dark:bg-[#f2f2f7] data-[state=checked]:translate-x-[calc(100%-2px)] data-[state=unchecked]:translate-x-0",
        )}
      />
    </RadixSwitchRoot>
  );
}

export { Switch };
